import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText, CheckCircle2, AlertTriangle, Coins, Award, FilePlus2, Archive,
} from "lucide-react";
import type { ScenarioRow } from "@/lib/scenarios";
import {
  buildTimelineEvents, summarize, computeLoanStatus,
  LOAN_STATUSES, TIMELINE_CATEGORY_LABEL,
  type TimelineCategory, type LoanStatus, type PaymentActualRow,
} from "@/lib/timeline";

type DateRange = "30d" | "90d" | "12m" | "all";

const DATE_LABEL: Record<DateRange, string> = {
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "12m": "Last 12 months",
  "all": "Lifetime",
};

const CATEGORY_ICON: Record<TimelineCategory, React.ComponentType<{ className?: string }>> = {
  loan_created: FilePlus2,
  payment_confirmed: CheckCircle2,
  payment_issue: AlertTriangle,
  lump_sum: Coins,
  stewardship: Award,
  report: FileText,
  archived: Archive,
};

const CATEGORY_TONE: Record<TimelineCategory, string> = {
  loan_created: "bg-primary/15 text-primary border-primary/30",
  payment_confirmed: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  payment_issue: "bg-destructive/15 text-destructive border-destructive/30",
  lump_sum: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  stewardship: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
  report: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
  archived: "bg-muted text-muted-foreground border-border",
};

const STATUS_TONE: Record<LoanStatus, string> = {
  Draft: "bg-muted text-muted-foreground",
  Active: "bg-primary/15 text-primary",
  "On Track": "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  "Ahead of Schedule": "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  "Behind Schedule": "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  "Needs Review": "bg-destructive/15 text-destructive",
  "Paid Off": "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  Archived: "bg-muted text-muted-foreground",
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtMoney(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function withinRange(iso: string, range: DateRange): boolean {
  if (range === "all") return true;
  const now = Date.now();
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return true;
  const days = range === "30d" ? 30 : range === "90d" ? 90 : 365;
  return now - t <= days * 24 * 60 * 60 * 1000;
}

export function LoanTimeline({
  userId,
  viewerMode = "owner",
}: {
  userId: string;
  viewerMode?: "owner" | "advisor";
}) {
  const [scenarios, setScenarios] = useState<ScenarioRow[] | null>(null);
  const [actuals, setActuals] = useState<PaymentActualRow[]>([]);
  const [category, setCategory] = useState<"all" | TimelineCategory>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | LoanStatus>("all");
  const [range, setRange] = useState<DateRange>("12m");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [sc, ac] = await Promise.all([
        supabase.from("scenarios").select("*").eq("user_id", userId).order("updated_at", { ascending: false }),
        supabase
          .from("scenario_payment_actuals")
          .select("scenario_id,payment_number,planned_date,planned_amount,status,actual_amount,actual_date,note")
          .eq("user_id", userId),
      ]);
      if (cancelled) return;
      if (sc.error) toast.error(sc.error.message);
      if (ac.error) toast.error(ac.error.message);
      setScenarios((sc.data as ScenarioRow[]) ?? []);
      setActuals((ac.data as PaymentActualRow[]) ?? []);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const events = useMemo(() => {
    if (!scenarios) return [];
    return buildTimelineEvents(scenarios, actuals);
  }, [scenarios, actuals]);

  const summary = useMemo(() => {
    if (!scenarios) return null;
    return summarize(scenarios, actuals);
  }, [scenarios, actuals]);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (category !== "all" && e.category !== category) return false;
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (!withinRange(e.date, range)) return false;
      return true;
    });
  }, [events, category, statusFilter, range]);

  if (scenarios === null) {
    return <p className="text-sm text-muted-foreground">Loading your timeline…</p>;
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
          {viewerMode === "advisor" ? "Client Planning Activity" : "Your Family Loan Timeline™"}
        </h2>
        <p className="text-sm text-muted-foreground">
          See the financial commitments your family has created, tracked, and followed through on.
        </p>
      </header>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard label="Total Commitments" value={summary.totalCommitments} />
          <SummaryCard label="Active Loans" value={summary.activeLoans} />
          <SummaryCard label="Paid-Off Loans" value={summary.paidOffLoans} />
          <SummaryCard label="Amount Modeled" value={fmtMoney(summary.totalAmountModeled)} />
          <SummaryCard label="Paid to Date" value={fmtMoney(summary.totalPaidToDate)} />
          <SummaryCard
            label="Avg Stewardship"
            value={summary.avgStewardshipScore == null ? "—" : `${summary.avgStewardshipScore}/100`}
          />
        </div>
      )}

      <Card className="p-3 flex flex-wrap items-center gap-2">
        <Select value={category} onValueChange={(v) => setCategory(v as "all" | TimelineCategory)}>
          <SelectTrigger className="h-9 w-[180px] text-sm"><SelectValue placeholder="Activity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All activity</SelectItem>
            {(Object.keys(TIMELINE_CATEGORY_LABEL) as TimelineCategory[]).map((c) => (
              <SelectItem key={c} value={c}>{TIMELINE_CATEGORY_LABEL[c]}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "all" | LoanStatus)}>
          <SelectTrigger className="h-9 w-[180px] text-sm"><SelectValue placeholder="Loan status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {LOAN_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={range} onValueChange={(v) => setRange(v as DateRange)}>
          <SelectTrigger className="h-9 w-[170px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(Object.keys(DATE_LABEL) as DateRange[]).map((r) => (
              <SelectItem key={r} value={r}>{DATE_LABEL[r]}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto text-xs text-muted-foreground">{filtered.length} events</div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No activity in this view. Try widening the filters.
        </Card>
      ) : (
        <ol className="relative border-l border-border pl-6 space-y-4">
          {filtered.map((e) => {
            const Icon = CATEGORY_ICON[e.category];
            return (
              <li key={e.id} className="relative">
                <span
                  className={`absolute -left-[34px] flex h-7 w-7 items-center justify-center rounded-full border ${CATEGORY_TONE[e.category]}`}
                  aria-hidden="true"
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <Card className="p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{e.title}</span>
                        {e.status && (
                          <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[e.status]}`}>
                            {e.status}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>{fmtDate(e.date)}</span>
                        {e.loanId && <span className="font-mono">{e.loanId}</span>}
                        {e.loanName && <span className="truncate max-w-[200px]">{e.loanName}</span>}
                        {e.purpose && <span>· {e.purpose}</span>}
                      </div>
                      {(e.description || e.amount != null) && (
                        <div className="text-sm mt-1">
                          {e.description ||
                            (e.amount != null ? fmtMoney(e.amount) : "")}
                        </div>
                      )}
                    </div>
                    {viewerMode === "owner" && (
                      <Button asChild size="sm" variant="outline" className="shrink-0">
                        <Link to={`/?scenario=${e.scenarioId}`}>Open Scenario</Link>
                      </Button>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card className="p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums mt-0.5">{value}</div>
    </Card>
  );
}

export { computeLoanStatus };